import { canonicalize } from "./canonical.js";
import { decryptPayload, encryptPayload, utf8ToBytes } from "./crypto.js";
import type { Envelope } from "./crypto.js";
import type { Op } from "./op.js";
import type { OpStore } from "./store.js";
import { validateAndAdd } from "./validate.js";

export interface Bundle {
  v: 1;
  group: string;
  ops: Op[];
}

// Ops the remote side (advertising `remoteHeads`) is missing, topo-ordered.
// With no heads given, the bundle is the complete history - used for invites,
// QR sync, and relay snapshots.
export function makeBundle(store: OpStore, remoteHeads: string[] = []): Bundle {
  return { v: 1, group: store.groupId, ops: store.missingFor(remoteHeads) };
}

export function encodeBundle(bundle: Bundle): string {
  return canonicalize(bundle as never);
}

export function decodeBundle(text: string): Bundle {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("bundle is not valid JSON");
  }
  const b = parsed as Bundle;
  if (b === null || typeof b !== "object" || b.v !== 1 || typeof b.group !== "string" || !Array.isArray(b.ops)) {
    throw new Error("bundle shape invalid");
  }
  return b;
}

export interface IngestResult {
  added: number;
  ops: Op[]; // the ops actually accepted this call, in acceptance order
  rejected: Array<{ hash: string; reason: string }>;
}

// Idempotent, order-tolerant ingest. Ops are admitted once their parents are
// present; a fixpoint loop covers bundles that arrive out of topo order. Ops
// whose parents never arrive in this bundle are rejected (stateless - the next
// cumulative pull retries them from scratch).
export function ingestBundle(store: OpStore, bundle: Bundle): IngestResult {
  const result: IngestResult = { added: 0, ops: [], rejected: [] };
  if (store.size > 0 && bundle.group !== "" && bundle.group !== store.groupId) {
    for (const op of bundle.ops) result.rejected.push({ hash: op.hash, reason: "foreign group" });
    return result;
  }
  let pending = bundle.ops.filter((op) => op && typeof op.hash === "string" && !store.has(op.hash));
  let progress = true;
  while (progress && pending.length > 0) {
    progress = false;
    const next: Op[] = [];
    for (const op of pending) {
      const parentsKnown =
        op.kind === "genesis" || (Array.isArray(op.parents) && op.parents.every((p) => store.has(p)));
      if (!parentsKnown) {
        next.push(op);
        continue;
      }
      const outcome = validateAndAdd(store, op);
      if (outcome.ok) {
        result.added++;
        result.ops.push(op);
        progress = true;
      } else {
        result.rejected.push({ hash: op.hash, reason: outcome.reason });
      }
    }
    pending = next;
  }
  for (const op of pending) result.rejected.push({ hash: op.hash, reason: "missing parent" });
  return result;
}

// Envelope forms - what actually travels over a relay or inside a QR code.

const decoder = new TextDecoder();

export async function encryptBundle(groupKey: Uint8Array, bundle: Bundle): Promise<Envelope> {
  return encryptPayload(groupKey, utf8ToBytes(encodeBundle(bundle)));
}

export async function decryptBundle(groupKey: Uint8Array, envelope: Envelope): Promise<Bundle> {
  const plaintext = await decryptPayload(groupKey, envelope);
  return decodeBundle(decoder.decode(plaintext));
}
